import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import {
  optimaCashReportExport,
  optimaSaleInvoiceAndTransfersExport,
  optimaVatMarginExport,
  optimaCostInvoiceExport,
  optimaSaleAsCostInvoicesExport,
  optimaCreditCardsTransfersExport,
  optimaSalesInvoiceAsPurchaseInvoiceExport,
} from "@/requests/bookkeepping";

import { FILTER_NAMES } from "@/static/filterNames";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";

export interface BookkeepingOptimaStateInterface {
  recordStampId: number | null;
  cashReportExportFilters: Array<CriterionItemRequest> | null;
  saleInvoiceAndTransfersExportFilters: Array<CriterionItemRequest> | null;
  vatMarginExportFilters: Array<CriterionItemRequest> | null;
  costInvoiceExportFilters: Array<CriterionItemRequest> | null;
  saleAsCostInvoicesExportFilters: Array<CriterionItemRequest> | null;
  creditCardsTransfersExportFilters: Array<CriterionItemRequest> | null;
  salesInvoiceAsPurchaseInvoiceExportFilters: Array<CriterionItemRequest> | null;
  isLoading: boolean;
}

export const BookkeepingOptimaState: BookkeepingOptimaStateInterface = {
  recordStampId: null,
  cashReportExportFilters: null,
  saleInvoiceAndTransfersExportFilters: null,
  vatMarginExportFilters: null,
  costInvoiceExportFilters: null,
  saleAsCostInvoicesExportFilters: null,
  creditCardsTransfersExportFilters: null,
  salesInvoiceAsPurchaseInvoiceExportFilters: null,
  isLoading: true,
};

const BookkeepingOptimaModule = defineModule({
  state: BookkeepingOptimaState as BookkeepingOptimaStateInterface,
  namespaced: true as const,
  modules: {},
  getters: {},
  mutations: {
    setCashReportExportFilters(state, param: Array<CriterionItemRequest> | null) {
      state.cashReportExportFilters = param;
    },
    setSaleInvoiceAndTransfersExportFilters(state, param: Array<CriterionItemRequest> | null) {
      state.saleInvoiceAndTransfersExportFilters = param;
    },
    setVatMarginExportFilters(state, param: Array<CriterionItemRequest> | null) {
      state.vatMarginExportFilters = param;
    },
    setCostInvoiceExportFilters(state, param: Array<CriterionItemRequest> | null) {
      state.costInvoiceExportFilters = param;
    },
    setSaleAsCostInvoicesExportFilters(state, param: Array<CriterionItemRequest> | null) {
      state.saleAsCostInvoicesExportFilters = param;
    },
    setCreditCardsTransfersExportFilters(state, param: Array<CriterionItemRequest> | null) {
      state.creditCardsTransfersExportFilters = param;
    },
    setSalesInvoiceAsPurchaseInvoiceExportFilters(state, param: Array<CriterionItemRequest> | null) {
      state.salesInvoiceAsPurchaseInvoiceExportFilters = param;
    },
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setRecordStampDialog(state, param: number | null) {
      state.recordStampId = param;
    },
  },
  actions: {
    async exportCashReport(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      await optimaCashReportExport(state.cashReportExportFilters, FILTER_NAMES.OptimaExportCashReportStandardFilter);
      commit.setIsLoading(false);
    },
    async exportSaleInvoiceAndTransfers(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      await optimaSaleInvoiceAndTransfersExport(
        state.saleInvoiceAndTransfersExportFilters,
        FILTER_NAMES.OptimaExportInvoicesAndTransfersStandardFilter
      );
      commit.setIsLoading(false);
    },
    async exportVatMargin(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      await optimaVatMarginExport(state.vatMarginExportFilters, FILTER_NAMES.OptimaExportVatMarginStandardFilter);
      commit.setIsLoading(false);
    },
    async exportCostInvoice(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      await optimaCostInvoiceExport(state.costInvoiceExportFilters, FILTER_NAMES.OptimaExportCostInvoiceStandardFilter);
      commit.setIsLoading(false);
    },
    async exportSaleAsCostInvoices(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      await optimaSaleAsCostInvoicesExport(
        state.saleAsCostInvoicesExportFilters,
        FILTER_NAMES.OptimaExportSaleAsCostInvoicesStandardFilter
      );
      commit.setIsLoading(false);
    },
    async exportCreditCardsTransfers(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      await optimaCreditCardsTransfersExport(
        state.creditCardsTransfersExportFilters,
        FILTER_NAMES.OptimaExportCreditCardsTransfersStandardFilter
      );
      commit.setIsLoading(false);
    },
    async exportSalesInvoiceAsPurchaseInvoice(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      await optimaSalesInvoiceAsPurchaseInvoiceExport(
        state.salesInvoiceAsPurchaseInvoiceExportFilters,
        FILTER_NAMES.OptimaExportSalesInvoiceAsPurchaseInvoiceFilter
      );
      commit.setIsLoading(false);
    },
  },
});

export default BookkeepingOptimaModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, BookkeepingOptimaModule);
